import { useCallback, useState } from 'react';

import { useApiUrl } from '@refinedev/core';

import { AxiosRequestConfig, Method } from 'axios';

import { axiosInstance } from '@/utility';

export type UseApiState<T> = {
  data: T | null;
  loading: boolean;
  error: string | null;
};

export type UseApiReturnType<T> = UseApiState<T> & {
  sendRequest: (
    endpoint: string,
    method: Method,
    payload?: any,
    config?: AxiosRequestConfig,
  ) => Promise<void>;
  sendBasicRequest: (
    endpoint: string,
    method: Method,
    payload?: any,
    config?: AxiosRequestConfig
  ) => Promise<any>;
  sendCustomURLBasicRequest: (
    endpoint: string,
    method: Method,
    payload?: any,
    config?: AxiosRequestConfig
  ) => Promise<any>;
};

export const useApi = <T>(dataProviderName?: string): UseApiReturnType<T> => {
  const apiUrl = useApiUrl(dataProviderName);
  const [state, setState] = useState<UseApiState<T>>({
    data: null,
    loading: false,
    error: null,
  });

  const sendRequest = useCallback(async (
    endpoint: string,
    method: Method,
    payload?: any,
    config: AxiosRequestConfig = {},
  ) => {
    setState({ data: null, loading: true, error: null });

    // Constructing the request configuration
    const fullUrl = `${apiUrl}${endpoint}`;
    const requestConfig: AxiosRequestConfig = {
      url: fullUrl,
      method,
      ...config,
    };

    // If the method is not GET, attach the payload as data
    if (method.toLowerCase() !== 'get' && payload) {
      requestConfig.data = payload;
    } else if (payload) {
      // For GET requests, append payload as params
      requestConfig.params = { ...requestConfig.params, ...payload };
    }

    try {
      const response = await axiosInstance(requestConfig);
      setState({ data: response.data, loading: false, error: null });
    } catch (error: any) {
      setState({ data: null, loading: false, error: error || 'An error occurred' });
    }
  }, []);

  async function sendBasicRequest(
    endpoint: string,
    method: Method,
    payload?: any,
    config: AxiosRequestConfig = {},
  ) {
    // Constructing the request configuration
    const fullUrl = `${apiUrl}${endpoint}`;
    const requestConfig: AxiosRequestConfig = {
      url: fullUrl,
      method,
      ...config,
    };

    // If the method is not GET, attach the payload as data
    if (method.toLowerCase() !== 'get' && payload) {
      requestConfig.data = payload;
    } else if (payload) {
      // For GET requests, append payload as params
      requestConfig.params = { ...requestConfig.params, ...payload };
    }

    try {
      const response = await axiosInstance(requestConfig);
      return response.data;
    } catch (error: any) {
      console.error('API Request Error');
      return error;
    }
  }

  const sendCustomURLBasicRequest = useCallback(async (
    url: string,
    method: Method,
    payload?: any,
    config: AxiosRequestConfig = {},
  ): Promise<T | null> => {
    const requestConfig: AxiosRequestConfig = {
      url,
      method,
      ...config,
    };

    if (method.toLowerCase() !== 'get' && payload) {
      requestConfig.data = payload;
    } else if (payload) {
      requestConfig.params = { ...requestConfig.params, ...payload };
    }

    try {
      const response = await axiosInstance(requestConfig);
      return response.data;
    } catch (error: any) {
      console.error('API Request Error');
      return null;
    }
  }, []);

  return {
    ...state,
    sendRequest,
    sendBasicRequest,
    sendCustomURLBasicRequest,
  };
};
